import { useState } from 'react';
import { sconti } from '../../api/listini';
import type { AnteprimaSconti, EsitoImportSconti } from '../../api/listini';
import { formattaPercentuale } from '../../lib/formato';
import { ErroreApi, ErroreRete } from '../../api/client';

/*  L'import del foglio di sconti esportato da Hopperix.

    TRE SCHERMATE IN UNA, nell'ordine in cui si lavora: si sceglie il file, si guarda cosa
    direbbe, si dice di chi è e si scrive.

    LA SCELTA DEL FORNITORE È IL PUNTO DELICATO, e per questo sta in mezzo e non è
    automatica. Il nome del file è una ragione sociale battuta a mano in un altro
    gestionale: «SABIANA S.P.A.» qui, «SABIANA SPA» in Mago; «MITSUBISHI ELECTRIC EUROPE
    B.V» contro «… B.V ITALIAN BRANCH». Meno di metà combaciano. E il più somigliante a
    «RUBINETTERIA PAFFONI» in quell'anagrafica è «RUBINETTERIE RITMONIO»: un nome
    vicinissimo e un'azienda diversa. Scegliere al posto di qualcuno vorrebbe dire
    scrivere gli sconti di una marca sotto un altro fornitore — nessun errore a schermo, e
    dei prezzi che cambiano.

    Quando il nome combacia a meno della punteggiatura e della forma societaria lo si
    propone già scelto, ma si vede ed è cambiabile: proporre non è decidere.

    LE RIGHE SCARTATE SI MOSTRANO, e non in fondo in piccolo. Un foglio di 154 righe ne
    produce 25: senza l'elenco di cosa è rimasto fuori e perché, quel numero sembra un
    guasto. Quasi tutte sono le categorie merceologiche di Hopperix, che in TeraWorks non
    esistono, e le righe dell'albero a sconto zero che stanno lì solo per tenere su i figli.
*/
export function ImportaSconti({ onImportato }: { onImportato: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [anteprima, setAnteprima] = useState<AnteprimaSconti | null>(null);
  const [fornitore, setFornitore] = useState('');
  const [esito, setEsito] = useState<EsitoImportSconti | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [mostraScartate, setMostraScartate] = useState(false);

  function racconta(guasto: unknown): string {
    if (guasto instanceof ErroreRete) return 'TeraWorks non ha risposto: non è stato scritto niente.';
    if (guasto instanceof ErroreApi) return guasto.message;
    return 'Non ha funzionato.';
  }

  async function guarda(scelto: File, codiceFornitore?: string) {
    setErrore(null);
    setEsito(null);
    setInCorso(true);

    try {
      const letta = await sconti.anteprimaHopperix(scelto, codiceFornitore);
      setAnteprima(letta);

      /*  Un solo nome che combacia davvero: lo si propone. Due, o nessuno, no — e in quel
          caso la tendina parte vuota e chi guarda deve scegliere.  */
      if (codiceFornitore === undefined) {
        const esatti = letta.fornitori.filter((f) => f.esatto);
        setFornitore(esatti.length === 1 ? (esatti[0]?.codiceMago ?? '') : '');
      }
    } catch (guasto) {
      setAnteprima(null);
      setErrore(racconta(guasto));
    } finally {
      setInCorso(false);
    }
  }

  async function scrivi() {
    if (file === null || fornitore === '') return;

    setErrore(null);
    setInCorso(true);

    try {
      setEsito(await sconti.importaHopperix(file, fornitore));
      setAnteprima(null);
      setFile(null);
      onImportato();
    } catch (guasto) {
      setErrore(racconta(guasto));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <section className="importa-sconti">
      <h3>Importa da Hopperix</h3>
      <p className="nota">
        Il foglio degli sconti esportato per un fornitore. Prima si guarda cosa dice, poi
        si scrive.
      </p>

      <input
        type="file"
        accept=".xlsx"
        onChange={(evento) => {
          const scelto = evento.target.files?.[0] ?? null;
          setFile(scelto);
          setEsito(null);
          if (scelto !== null) void guarda(scelto);
        }}
      />

      {errore !== null && <p className="errore">{errore}</p>}

      {anteprima !== null && (
        <>
          <p className="riassunto">
            <strong>{anteprima.nomeNelFile}</strong> — di {anteprima.righeLette} righe ne
            scriverebbe <strong>{anteprima.sconti.length}</strong>
            {anteprima.scartate.length > 0 && (
              <>
                {' '}
                <button type="button" className="collegamento"
                  onClick={() => setMostraScartate(!mostraScartate)}>
                  ({anteprima.scartate.length} fuori)
                </button>
              </>
            )}
          </p>

          <label className="campo-blocco">
            <span>Di che fornitore è</span>
            <select
              value={fornitore}
              onChange={(evento) => {
                setFornitore(evento.target.value);
                /*  Ricaricata con il fornitore scelto: solo allora si sa quanti articoli
                    tocca ogni riga e cosa c'è già. Prima sarebbero stati zeri inventati.  */
                if (file !== null && evento.target.value !== '') {
                  void guarda(file, evento.target.value);
                }
              }}
            >
              <option value="">— scegli —</option>
              {anteprima.fornitori.map((f) => (
                <option key={f.codiceMago} value={f.codiceMago}>
                  {f.denominazione} ({f.codiceMago}){f.esatto ? ' — combacia' : ''}
                </option>
              ))}
            </select>
          </label>

          {anteprima.fornitori.length === 0 && fornitore === '' && (
            <p className="nota">
              Nessun fornitore somigliante a «{anteprima.nomeNelFile}» in Mago. Va
              cercato a mano: il nome in Hopperix può essere scritto diversamente.
            </p>
          )}

          {mostraScartate && (
            <ul className="scartate">
              {anteprima.scartate.map((s) => (
                <li key={s.numero}>
                  <em>riga {s.numero}</em> {s.descrizione} — {s.motivo}
                </li>
              ))}
            </ul>
          )}

          <table className="tabella-sconti">
            <thead>
              <tr>
                <th>Marca</th>
                <th>Famiglia</th>
                <th className="numero">Sconto</th>
                <th className="numero">Articoli</th>
                <th>Dal</th>
                <th>Al</th>
              </tr>
            </thead>
            <tbody>
              {anteprima.sconti.map((s) => (
                <tr key={`${s.siglaProduttore}-${s.codiceFamigliaSconto ?? ''}`}
                    className={s.giaConcordato ? 'gia' : undefined}>
                  <td>
                    {s.siglaProduttore}
                    {s.produttore !== null && <em> {s.produttore}</em>}
                  </td>
                  <td>{s.codiceFamigliaSconto ?? '—'}</td>
                  <td className="numero">{formattaPercentuale(s.percentuale)}</td>
                  {/*  Zero articoli non impedisce niente: un accordo si scrive anche
                       prima del primo listino, ed è anzi il momento giusto. Va però
                       detto, perché «questa riga non tocca nessun prezzo» e «ne tocca
                       seicento» sono due decisioni diverse.  */}
                  <td className="numero">{fornitore === '' ? '' : s.articoli}</td>
                  <td>{s.decorrenza ?? ''}</td>
                  <td>{s.scadenza ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            type="button"
            className="primario"
            disabled={inCorso || fornitore === '' || anteprima.sconti.length === 0}
            onClick={() => void scrivi()}
          >
            {inCorso ? 'Scrivo…' : `Scrivi ${anteprima.sconti.length} accordi`}
          </button>
        </>
      )}

      {esito !== null && (
        <div className="esito-import">
          <p>
            <strong>{esito.scritti}</strong> accordi scritti su {esito.fornitore}.
            {esito.saltati.length > 0 && ` ${esito.saltati.length} saltati.`}
          </p>
          {esito.saltati.length > 0 && (
            <ul className="scartate">
              {esito.saltati.map((s) => (
                <li key={`${s.numero}-${s.descrizione}`}>
                  {s.descrizione} — {s.motivo}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
